import { Loading, Modal, Tooltip } from '@carbon/react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useDeleteEntityMutation } from 'api/entityEndpoints';
import sharedMessages from 'Components/utils/sharedMessages';
import CommonButton from 'Components/Common/CommonButton/CommonButton';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { UserRole } from 'api/dtos/user';
import styles from './RemoveEntityButton.module.scss';

const RemoveEntityButton = ({
    id,
    role,
}: {
    id: string;
    role: UserRole;
}) => {
    const intl = useIntl();
    const { isOwner } = useGetUserPermission();
    const isCurrentUserOwner = role === UserRole.Owner;
    const isDisabled = !isOwner || isCurrentUserOwner;

    const [deleteEntity, { isLoading: isLoadingDelete }] = useDeleteEntityMutation();
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const onRemoveRequest = () => {
        setConfirmationModalOpen(true);
    };

    const onModalClose = () => {
        setConfirmationModalOpen(false);
    };

    const onSubmit = async () => {
        try {
            await deleteEntity(id);
        } catch (e: unknown) {
            // TO_DO
            console.log(e);
        }
        setConfirmationModalOpen(false);
    };

    const button = (
        <CommonButton
            label={intl.formatMessage(messages.removeLabel)}
            kind="ghost"
            disabled={isDisabled}
            iconSize={21}
            onClick={onRemoveRequest}
        />
    );

    return (
        <>
            {isDisabled ? (
                <Tooltip
                    label={intl.formatMessage(
                        isCurrentUserOwner ? messages.ownerRemoveTooltip : messages.permissionRemoveTooltip,
                    )}
                    align="bottom"
                >
                    <span className={styles.tooltipWrapper}>
                        {button}
                    </span>
                </Tooltip>
            ) : button}
            <Modal
                preventCloseOnClickOutside
                open={confirmationModalOpen}
                onRequestClose={onModalClose}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onRequestSubmit={onSubmit}
                danger
                modalHeading={intl.formatMessage(messages.areYouSure)}
                modalLabel={intl.formatMessage(messages.removingEntity)}
                primaryButtonText={intl.formatMessage(sharedMessages.remove)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            />
            {isLoadingDelete && <Loading />}
        </>
    );
};

const messages = defineMessages({
    removeLabel: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.RemoveEntityButton.RemoveEntityButton.removeLabel',
        defaultMessage: 'Usuń grupę',
    },
    removingEntity: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.RemoveEntityButton.RemoveEntityButton.removingEntity',
        defaultMessage: 'Usuwanie grupy',
    },
    areYouSure: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.RemoveEntityButton.RemoveEntityButton.areYouSure',
        defaultMessage: 'Jesteś pewien, że chcesz usunąć grupę? Ta operacja jest nieodwracalna.',
    },
    ownerRemoveTooltip: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.RemoveEntityButton.RemoveEntityButton.ownerRemoveTooltip',
        defaultMessage: 'Nie możesz usunąć siebie jako właściciela. Najpierw przekaż własność tej parafii lub grupy innej osobie.',
    },
    permissionRemoveTooltip: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.RemoveEntityButton.RemoveEntityButton.permissionRemoveTooltip',
        defaultMessage: 'Tylko właściciel może usuwać grupy.',
    },

});

export default RemoveEntityButton;
